// Light the halls' art draws itself (docs/166 C2, docs/168 "coloured light falls in slanted panes, never as a
// wheel"): the one shaft from a hole in the roof (look.json `roof_shaft`), the coloured panes a shrine's glass
// throws, the warm shafts from a temple's slits. All slanted down and right, in whole steps, with no dither:
// light on stone, never a mark. Over the ground where it falls on a square that has art, and over a wall's own
// pixels as a standing piece sorted just after that wall, so it never lands on the void.
import type { Standing } from '../types';
import { hex, type Canvas, type RGBA } from './canvas';
import { fromOklab, oklab } from './colour';
import { C, type Ctx } from './context';
import { isLiquid, type Ground } from './ground';
import { dressAt, frontAt } from './halls-dress';

type Grounds = ReadonlyArray<Ground | undefined>;

interface Shaft { tint: string; steps: [number, number]; slant: number; width: number }

/** Light on a pixel of the board: the ground's, or a wall's own (its pixels tinted in a new standing piece). */
class Light {
  private readonly over = new Map<Standing, Uint8ClampedArray>();
  private readonly walls = new Map<number, Standing[]>();
  constructor(readonly ctx: Ctx, readonly out: Canvas, readonly standing: Standing[]) {
    for (const s of standing) if (s.kind === 'wall' && s.px) {
      const k = s.at.x;
      let l = this.walls.get(k);
      if (!l) this.walls.set(k, (l = []));
      l.push(s);
    }
    for (const l of this.walls.values()) l.sort((a, b) => b.foot - a.foot);
  }
  /** Whether the ground shows at board px (X, Y): an open square of the board. */
  ground(X: number, Y: number): boolean {
    const x = Math.floor(X / C), y = Math.floor(Y / C);
    return this.ctx.sq.on(x, y) && !this.ctx.sq.solid(x, y) && this.out.inside(X, Y);
  }
  /** The wall image drawn on top at (X, Y), and its pixel's index there. */
  wall(X: number, Y: number): [Standing, number] | undefined {
    const l = this.walls.get(Math.floor(X / C));
    if (!l) return undefined;
    for (const s of l) {
      const u = X - s.x, v = Y - s.y;
      if (u < 0 || v < 0 || u >= s.w || v >= s.h) continue;
      const i = (v * s.w + u) * 4;
      if (s.px![i + 3]) return [s, i];
    }
    return undefined;
  }
  /** Move the pixel toward `tint` by `a` (the scripts' `p + (tint - p) * a`). */
  tint(X: number, Y: number, tint: RGBA, a: number, onWalls: boolean): void {
    const w = onWalls ? this.wall(X, Y) : undefined;
    if (w) {
      const [s, i] = w;
      let o = this.over.get(s);
      if (!o) this.over.set(s, (o = new Uint8ClampedArray(s.w * s.h * 4)));
      if (o[i + 3]) return;
      o[i] = tint[0]; o[i + 1] = tint[1]; o[i + 2] = tint[2]; o[i + 3] = Math.round(a * 255);
      return;
    }
    if (!this.ground(X, Y)) return;
    const i = (Y * this.out.w + X) * 4, p = this.out.px;
    p[i] = p[i]! + (tint[0] - p[i]!) * a; p[i + 1] = p[i + 1]! + (tint[1] - p[i + 1]!) * a; p[i + 2] = p[i + 2]! + (tint[2] - p[i + 2]!) * a;
  }
  private readonly memo = new Map<number, number>();
  /** Lighten the ground pixel in OKLab (the panes and the slits); `key` (0..63) names `f` among this board's few, so each colour is worked out once. */
  lab(X: number, Y: number, key: number, f: (L: number, a: number, b: number) => [number, number, number]): void {
    if (!this.ground(X, Y)) return;
    const i = (Y * this.out.w + X) * 4, p = this.out.px;
    const rgb = (p[i]! << 16) | (p[i + 1]! << 8) | p[i + 2]!, k = rgb * 64 + key;
    let c = this.memo.get(k);
    if (c === undefined) {
      const o = fromOklab(...f(...oklab([p[i]!, p[i + 1]!, p[i + 2]!, 255])));
      c = (o[0] << 16) | (o[1] << 8) | o[2];
      this.memo.set(k, c);
    }
    p[i] = c >>> 16; p[i + 1] = (c >>> 8) & 255; p[i + 2] = c & 255;
  }
  /** The tinted wall pixels as standing pieces, each sorted just after its wall. */
  finish(): void {
    for (const [s, px] of this.over) this.standing.push({ kind: 'piece', at: s.at, x: s.x, y: s.y, w: s.w, h: s.h, foot: s.foot + 0.005, px });
  }
}

/**
 * One shaft a board (one per flooded hall at most for a cistern: only where it lands on water), from the lip of a
 * front facing a room, slanted down and right over the front and the floor, with a low flat oval where it lands.
 * Ferns grow only under it (the jungle's `gap_fern`, `decal_fern`).
 */
export function roofShaft(ctx: Ctx, out: Canvas, grounds: Grounds, standing: Standing[]): void {
  const Sh = ctx.look['roof_shaft'] as Shaft | undefined;
  if (!Sh) return;
  const { sq } = ctx, { w, h } = sq;
  const rise = ctx.look.wall_rise ?? 0;
  const wet = (x: number, y: number): boolean => { const g = sq.on(x, y) ? grounds[y * w + x] : undefined; return !!g && isLiquid(g.name); };
  const open = (x: number, y: number): boolean => sq.on(x, y) && !sq.solid(x, y);
  const div = Sh.slant <= 1 ? 2 : Sh.slant;
  const fall = 4;   // squares it falls below the front
  let best: [number, number] | undefined, bestR = Infinity;
  for (let y = 0; y < h - fall; y++) for (let x = 0; x < w - 2; x++) {
    if (!frontAt(ctx, x, y)) continue;
    // it lands in a room: the squares under its path are open
    let ok = true, water = false;
    for (let j = 1; j <= fall && ok; j++) for (let i = 0; i <= 2; i++) {
      const a = x + Math.floor((j * C) / div / C) + i;
      if (!open(a, y + j)) { ok = false; break; }
      if (wet(a, y + j)) water = true;
    }
    if (!ok || (ctx.set.kit === 'cistern' && !water)) continue;
    const r = ctx.roll('roof shaft', x, y);
    if (r < bestR) { bestR = r; best = [x, y]; }
  }
  if (!best) return;
  const [fx, fy] = best;
  const tint = hex(Sh.tint), ww = Sh.width, edge = Math.max(5, Math.round(ww / 6));
  const ytop = fy * C - rise + 1, y1 = (fy + fall) * C + 6;
  const x0 = fx * C + C / 2 - Math.floor(ww / 2) + 6;
  // ferns under the gap, before the light falls on them
  const ferns = ['gap_fern', 'decal_fern'].filter((p) => ctx.has(p));
  if (ferns.length) {
    for (let j = fall - 1; j <= fall + 1; j++) for (let i = -1; i <= 3; i++) {
      const x = fx + Math.floor((fall * C) / div / C) + i, y = fy + j;
      if (!open(x, y) || wet(x, y) || sq.tile(x, y)?.exit) continue;
      const heart = i >= 0 && i <= 2 && j === fall;
      if (ctx.roll('fern', x, y) % 100 >= (heart ? 50 : 15)) continue;
      const name = ferns[ctx.roll('fern kind', x, y) % ferns.length]!;
      const p = ctx.pick(name, 'fern pick', x, y)!;
      out.draw(p, x * C + C / 2 - p.w / 2, y * C + C - p.h);
    }
  }
  const L = new Light(ctx, out, standing);
  for (let Y = ytop; Y < y1; Y++) {
    const xa = x0 + Math.floor((Y - ytop) / div);
    for (let dx = 0; dx < ww; dx++) L.tint(xa + dx, Y, tint, dx >= edge && dx < ww - edge ? Sh.steps[0] : Sh.steps[1], true);
  }
  const cx = x0 + Math.floor((y1 - ytop) / div) + Math.floor(ww / 2), cy = y1, rx = Math.max(28, ww - 10), ry = 11;
  for (let Y = cy; Y <= cy + ry; Y++) for (let X = cx - rx; X <= cx + rx; X++) {
    const d = ((X - cx) / rx) ** 2 + ((Y - cy) / ry) ** 2;
    if (d < 1) L.tint(X, Y, tint, d < 0.55 ? Sh.steps[0] : Sh.steps[1], false);
  }
  L.finish();
}

// ---- coloured panes from a shrine's glass, warm shafts from a temple's slits ----------------------------------------

/** Each shrine's glass, as (a, b) shifts a pane at a time (`GLASS` in each `shrine_board.py.txt`). */
const GLASS: Record<string, Array<[number, number]>> = {
  boreal: [[-0.075, -0.004], [-0.02, -0.09], [0.05, -0.075], [-0.06, 0.006], [0.03, 0.06], [-0.03, -0.07]],
  wetland: [[-0.012, 0.012], [0, 0.016], [-0.016, 0.008], [-0.006, -0.036], [-0.01, 0.014], [-0.014, 0.006]],
  jungle: [[-0.03, -0.006], [-0.012, -0.022], [-0.034, -0.004], [-0.008, -0.028], [-0.03, -0.008], [0.008, 0.016], [-0.012, -0.02], [-0.028, -0.006]],
  desert: [[-0.05, -0.03], [-0.03, -0.05], [-0.05, -0.016], [-0.04, -0.05], [0.006, 0.032], [-0.05, -0.03], [-0.03, -0.05], [-0.04, -0.02]],
  highland: [[-0.012, -0.006], [-0.004, 0.004], [-0.014, -0.01], [0.03, -0.02], [-0.008, -0.004], [-0.002, 0.006], [-0.012, -0.008], [-0.006, 0.002]],
};
const WINDOWS = new Set(['wall_face_lancet', 'wall_face_glass', 'wall_face_rose', 'wall_face_qamariya']);

/** Panes of light down and right from every window a shrine's fronts carry; a temple's slits throw warm sun the same way. */
export function windowLight(ctx: Ctx, out: Canvas, grounds: Grounds, standing: Standing[]): void {
  const { sq } = ctx, { w, h } = sq;
  const L = new Light(ctx, out, standing);
  const water = (X: number, Y: number): boolean => { const x = Math.floor(X / C), y = Math.floor(Y / C); const g = sq.on(x, y) ? grounds[y * w + x] : undefined; return !!g && isLiquid(g.name); };
  const roll = (X: number, Y: number): number => ctx.roll('fall', X >> 1, Y >> 1);
  const biome = ctx.set.biome;
  for (let y = 0; y < h - 1; y++) for (let x = 0; x < w; x++) {
    const d = dressAt(ctx, x, y);
    if (!d) continue;
    const top = (y + 1) * C;
    if (ctx.set.kit === 'shrine' && WINDOWS.has(d)) {
      if (biome === 'ashlands' || biome === 'underdark') {
        // the alabaster: dim panes, three across, buff with a dull red now and then; the board stays grey
        const n = 60;
        for (let Y = top; Y < top + n; Y++) {
          const pr = (Y - top) / n, cx = x * C + 12 + pr * 24, half = 5 + pr * 4;
          for (let X = Math.floor(cx - half); X <= Math.floor(cx + half); X++) {
            const u = (X - cx + half) / (2 * half);
            if (Math.abs(((u * 3) % 1) - 0.5) > 0.42 || Y % 9 === 0 || water(X, Y)) continue;
            const red = Math.floor(u * 3) === 1 && Math.floor((Y - top) / 9) % 3 === 0;
            const c: [number, number] = biome === 'underdark' ? [-0.024, -0.006] : red ? [0.024, 0.01] : [0.001, 0.012];
            const k = Math.round(0.8 * (1 - pr * 0.7) * 5) / 5;
            if (k <= 0.04) continue;
            L.lab(X, Y, Math.round(k * 5) * 2 + (red ? 1 : 0), (Lv, a, b) => [Lv * (1 + 0.17 * k) + 0.018 * k, a + c[0] * k, b + c[1] * k]);
          }
        }
        continue;
      }
      const glass = GLASS[biome] ?? GLASS['boreal']!;
      const rose = d === 'wall_face_rose';
      const n = rose ? 118 : 78, cols = rose ? 4 : 2;
      for (let Y = top; Y < top + n; Y++) {
        const pr = (Y - top) / n, cx = x * C + 12 + pr * (rose ? 42 : 30), half = (rose ? 13 : biome === 'jungle' ? 7 : 6) + pr * (rose ? 9 : 6);
        for (let X = Math.floor(cx - half); X <= Math.floor(cx + half); X++) {
          const u = (X - cx + half) / (2 * half);
          if (Math.abs(((u * cols) % 1) - 0.5) > (rose ? 0.47 : 0.46) || Y % 13 === 0) continue;
          if (pr > (rose ? 0.8 : 0.78) && roll(X, Y) % 3 === 0) continue;
          const t = (rose ? 0.8 : 0.95) * (1 - pr * (rose ? 0.7 : 0.65));
          const gi = ((rose ? Math.floor(u * 4) : Math.floor(u * 2)) + 3 * Math.floor(Y / 13) + x) % glass.length, c = glass[gi]!;
          const k = Math.round(t * 6) / 6;
          if (t <= 0.04) continue;
          L.lab(X, Y, Math.round(k * 6) * 8 + gi, (Lv, a, b) => [Lv * (1 + 0.34 * k) + 0.05 * k, a + c[0] * k, b + c[1] * k]);
        }
      }
    } else if (ctx.set.kit === 'temple' && (d === 'wall_face_slit' || d === 'wall_face_sun')) {
      // sun from the high slits: long shafts, whole steps of lightness toward pale gold
      const n = d === 'wall_face_slit' ? 96 : 150, half0 = d === 'wall_face_slit' ? 4 : 9;
      for (let Y = top; Y < top + n; Y++) {
        const pr = (Y - top) / n, cx = x * C + 12 + pr * 48, half = half0 + pr * 5;
        const t = 0.9 * (1 - pr * 0.7);
        if (t <= 0.04) continue;
        const k = Math.round(t * 5) / 5;
        for (let X = Math.floor(cx - half); X <= Math.floor(cx + half); X++) L.lab(X, Y, Math.round(k * 5), (Lv, a, b) => [Lv * (1 + 0.26 * k) + 0.03 * k, a + 0.006 * k, b + 0.03 * k]);
      }
    }
  }
}
